import { useQuery } from '@tanstack/react-query'
import type { PaginationState, SortingState } from '@tanstack/react-table'
import { Eye } from 'lucide-react'
import { useMemo, useState } from 'react'

import { archivedDocumentsQueryOptions, downloadArchivedDocument } from '@/api/documents-api'
import type { ArchivedDocument, DocumentKind } from '@/api/documents-types'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { ViewDocumentButton } from '@/components/document-viewer'
import { PageHeader } from '@/components/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatDateTime, formatShortDate } from '@/lib/datetime'

/** What each kind reads as on screen. The wire names are the server's; these are the teller's. */
const KIND_LABELS: Record<DocumentKind, string> = {
  SavingsReceipt: 'Savings receipt',
  LoanReceipt: 'Loan receipt',
  SavingsStatement: 'Savings statement',
  LoanStatement: 'Loan statement',
  LoanDisclosure: 'Loan disclosure',
  CollectionSheet: 'Collection sheet',
}

const KIND_ORDER: DocumentKind[] = [
  'SavingsReceipt',
  'LoanReceipt',
  'SavingsStatement',
  'LoanStatement',
  'LoanDisclosure',
  'CollectionSheet',
]

/** The Select's "no filter" value: Radix refuses an empty string as an item value. */
const ANY_KIND = 'any'

const UNSORTED: SortingState = []

/** One row's "Open" control. Its own component so the viewer dialog is per row. */
function OpenButton({ document }: { document: ArchivedDocument }) {
  return (
    <ViewDocumentButton
      variant="ghost"
      size="sm"
      title={document.title}
      description={`${KIND_LABELS[document.kind]} filed ${formatDateTime(document.generatedAtUtc)}${
        document.generatedByUsername ? ` by ${document.generatedByUsername}` : ''
      }. Reprinting serves this copy, exactly as it was handed over.`}
      load={() => downloadArchivedDocument(document)}
    >
      <Eye />
      Open
    </ViewDocumentButton>
  )
}

const columnHelper = dataTableColumns<ArchivedDocument>()

const COLUMNS: DataTableColumn<ArchivedDocument>[] = [
  columnHelper.accessor('generatedAtUtc', {
    header: 'Filed',
    cell: (context) => formatDateTime(context.row.original.generatedAtUtc),
    enableSorting: false,
    meta: { noWrap: true, card: 'secondary', cardLabel: 'Filed' },
  }),
  columnHelper.accessor('kind', {
    header: 'Kind',
    cell: (context) => <Badge tone="neutral">{KIND_LABELS[context.row.original.kind]}</Badge>,
    enableSorting: false,
    meta: { card: 'secondary', cardLabel: 'Kind' },
  }),
  columnHelper.accessor('title', {
    header: 'Document',
    cell: (context) => <span className="font-medium text-heading">{context.row.original.title}</span>,
    enableSorting: false,
    meta: { card: 'title' },
  }),
  columnHelper.accessor('businessDate', {
    header: 'Business date',
    cell: (context) => formatShortDate(context.row.original.businessDate),
    enableSorting: false,
    meta: { noWrap: true, card: 'lead' },
  }),
  columnHelper.accessor('branchId', {
    header: 'Branch',
    cell: (context) => `Branch ${context.row.original.branchId}`,
    enableSorting: false,
    meta: { noWrap: true, card: 'secondary', cardLabel: 'Branch' },
  }),
  columnHelper.accessor('generatedByUsername', {
    header: 'By',
    cell: (context) => context.row.original.generatedByUsername ?? '—',
    enableSorting: false,
    meta: { card: 'secondary', cardLabel: 'By' },
  }),
  columnHelper.display({
    id: 'open',
    header: '',
    cell: (context) => <OpenButton document={context.row.original} />,
    meta: { align: 'right', card: 'lead' },
  }),
]

/**
 * The archive of every paper document the system has generated.
 *
 * This is the "folder in the system" a kiosk counter opens: a receipt that was printed an hour ago
 * and needs printing again, a statement a customer says they never received, a collection sheet
 * from last week's meeting. Every row opens in the viewer, and a reprint serves the filed copy —
 * never a fresh render that might have moved on.
 *
 * The server scopes the list to the branches the caller can reach; the filters here narrow within
 * that. The search box matches the title — a receipt number, an account number — which is what a
 * teller reads off the paper in the customer's hand.
 */
export function DocumentsArchivePage() {
  const [kind, setKind] = useState<DocumentKind | undefined>(undefined)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [search, setSearch] = useState('')
  const [applied, setApplied] = useState<{ from?: string; to?: string; search?: string }>({})
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 })

  const query = useQuery(
    archivedDocumentsQueryOptions({
      kind,
      from: applied.from,
      to: applied.to,
      search: applied.search,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
  )

  const columns = useMemo(() => COLUMNS, [])

  const apply = () => {
    setApplied({
      from: from === '' ? undefined : from,
      to: to === '' ? undefined : to,
      search: search.trim() === '' ? undefined : search.trim(),
    })
    setPagination((current) => ({ ...current, pageIndex: 0 }))
  }

  const clear = () => {
    setKind(undefined)
    setFrom('')
    setTo('')
    setSearch('')
    setApplied({})
    setPagination((current) => ({ ...current, pageIndex: 0 }))
  }

  const filtered = kind !== undefined || applied.from !== undefined || applied.to !== undefined || applied.search !== undefined

  return (
    <>
      <PageHeader
        title="Documents"
        description="Every receipt, statement, disclosure and collection sheet the system has generated, on file for reprint. Opening one serves the copy that was handed over."
      />

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-card border border-border-subtle bg-surface p-3">
        <div className="w-48 space-y-1.5">
          <Label htmlFor="documents-kind">Kind</Label>
          <Select
            value={kind ?? ANY_KIND}
            onValueChange={(value) => {
              setKind(value === ANY_KIND ? undefined : (value as DocumentKind))
              setPagination((current) => ({ ...current, pageIndex: 0 }))
            }}
          >
            <SelectTrigger id="documents-kind">
              <SelectValue placeholder="Any kind" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_KIND}>Any kind</SelectItem>
              {KIND_ORDER.map((value) => (
                <SelectItem key={value} value={value}>
                  {KIND_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40 space-y-1.5">
          <Label htmlFor="documents-from">From</Label>
          <Input
            id="documents-from"
            type="date"
            value={from}
            max={to === '' ? undefined : to}
            onChange={(event) => setFrom(event.target.value)}
          />
        </div>
        <div className="w-40 space-y-1.5">
          <Label htmlFor="documents-to">To</Label>
          <Input
            id="documents-to"
            type="date"
            value={to}
            min={from === '' ? undefined : from}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>
        <div className="min-w-56 flex-1 space-y-1.5">
          <Label htmlFor="documents-search">Receipt or account number</Label>
          <Input
            id="documents-search"
            value={search}
            placeholder="e.g. 001-20260905-000123"
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                apply()
              }
            }}
          />
        </div>
        <Button variant="primary" size="sm" onClick={apply}>
          Apply
        </Button>
        {filtered ? (
          <Button variant="ghost" size="sm" onClick={clear}>
            Clear
          </Button>
        ) : null}
      </div>

      <DataTable
        columns={columns}
        data={query.data?.items}
        rowCount={query.data?.totalCount}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={UNSORTED}
        onSortingChange={() => undefined}
        isLoading={query.isPending}
        isFetching={query.isFetching}
        error={query.isError ? query.error : null}
        onRetry={() => void query.refetch()}
        getRowId={(row) => row.id}
        emptyTitle="Nothing on file yet"
        emptyDescription={
          filtered
            ? 'No documents match these filters. Try clearing them before concluding nothing was printed.'
            : 'Documents appear here the first time they are printed — a receipt at the counter, a statement from an account, a collection sheet from a meeting.'
        }
      />
    </>
  )
}
